import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, FileUp, X } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { CARRIER_DOCUMENT_TYPES, DOCUMENT_VERSION_OPTIONS } from "./mappers";

const fieldClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-fluid-sm dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text";

function parseIsoDate(value) {
  if (!value) return undefined;
  const parts = String(value).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return undefined;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function toIsoDate(date) {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplayDate(value) {
  const date = parseIsoDate(value);
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

/**
 * Upload one or more carrier documents (COI / W-9 / MSA / Other)
 * or rate documents (file only).
 *
 * kind: "carrier" | "rate"
 */
export default function UploadDocumentModal({
  carrierName,
  kind = "carrier",
  onClose,
  onUpload,
}) {
  const fileInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const [documentType, setDocumentType] = useState("COI");
  const [documentVersion, setDocumentVersion] = useState(1);
  const [expiryDate, setExpiryDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const isCarrier = kind === "carrier";
  const title = isCarrier ? "Upload Documents" : "Upload Rate Document";
  const selectedExpiry = parseIsoDate(expiryDate);

  useEffect(() => {
    if (!showDatePicker) return undefined;
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setShowDatePicker(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDatePicker]);

  const onFilesChosen = (list) => {
    const next = Array.from(list || []);
    if (!next.length) return;
    setFiles((prev) => {
      const names = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev];
      next.forEach((f) => {
        const key = `${f.name}:${f.size}`;
        if (!names.has(key)) merged.push(f);
      });
      return merged;
    });
    setError("");
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!files.length) {
      setError("Choose at least one file to upload.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      await onUpload({
        files,
        documentType: isCarrier ? documentType : undefined,
        documentVersion: isCarrier ? Number(documentVersion) || 1 : undefined,
        expiryDate: isCarrier && expiryDate ? expiryDate : undefined,
      });
    } catch (err) {
      setError(err?.message || "Upload failed");
      setUploading(false);
      return;
    }
    setUploading(false);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40 px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-lg space-y-fluid-4 rounded-2xl bg-white p-fluid-4 shadow-xl dark:bg-acs-dark-surface"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8EFF3] text-[#223688]">
              <FileUp size={18} />
            </div>
            <div>
              <h2 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                {title}
              </h2>
              <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext">
                {carrierName || "Carrier"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-acs-dark-text"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-fluid-3 rounded-lg border border-gray-200 bg-gray-50 p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-hover">
          {isCarrier ? (
            <>
              <div className="grid grid-cols-1 gap-fluid-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
                    Document type
                  </label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className={fieldClass}
                    disabled={uploading}
                  >
                    {CARRIER_DOCUMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
                    Version
                  </label>
                  <select
                    value={documentVersion}
                    onChange={(e) => setDocumentVersion(Number(e.target.value))}
                    className={fieldClass}
                    disabled={uploading}
                  >
                    {DOCUMENT_VERSION_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
                  Expiry date (optional)
                </label>
                <div className="relative" ref={datePickerRef}>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => setShowDatePicker((prev) => !prev)}
                    className={`${fieldClass} flex cursor-pointer items-center justify-between text-left`}
                  >
                    <span
                      className={
                        expiryDate
                          ? "text-gray-900 dark:text-acs-dark-text"
                          : "text-gray-400 dark:text-gray-500"
                      }
                    >
                      {expiryDate ? formatDisplayDate(expiryDate) : "Select date"}
                    </span>
                    <Calendar size={16} className="shrink-0 text-[#223688]" />
                  </button>
                  {showDatePicker ? (
                    <div className="absolute left-0 top-full z-[1100] mt-2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-acs-dark-border dark:bg-acs-dark-surface">
                      <DayPicker
                        mode="single"
                        selected={selectedExpiry}
                        onSelect={(day) => {
                          setExpiryDate(day ? toIsoDate(day) : "");
                          setShowDatePicker(false);
                        }}
                      />
                      {expiryDate ? (
                        <button
                          type="button"
                          className="mt-1 w-full rounded-md px-2 py-1 text-fluid-sm text-[#223688] hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
                          onClick={() => {
                            setExpiryDate("");
                            setShowDatePicker(false);
                          }}
                        >
                          Clear date
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}

          <div>
            <label className="mb-1 block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
              Files
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={isCarrier ? undefined : ".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"}
              className="hidden"
              onChange={(e) => {
                onFilesChosen(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="w-full rounded-md border border-dashed border-gray-300 bg-white px-3 py-4 text-fluid-sm text-gray-600 hover:border-[#223688] hover:text-[#223688] dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text"
            >
              {isCarrier
                ? "Click to choose files (PDF, images, or docs)"
                : "Click to choose PDF or image files (png, jpg, jpeg, webp)"}
            </button>
            {files.length ? (
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${f.size}-${idx}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-fluid-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                  >
                    <span className="truncate font-medium text-gray-800 dark:text-acs-dark-text">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => removeFile(idx)}
                      className="shrink-0 text-gray-400 hover:text-red-600"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {error ? (
              <p className="mt-2 text-fluid-sm text-red-600">{error}</p>
            ) : null}
          </div>
        </div>

        <div className="flex justify-end gap-fluid-2 border-t border-gray-200 pt-fluid-2 dark:border-acs-dark-border">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="rounded-full border border-gray-300 px-fluid-button-x py-fluid-button-y text-fluid-sm text-gray-700 hover:bg-gray-100 dark:border-acs-dark-border dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={uploading}
            className="rounded-full bg-[#223688] px-fluid-button-x py-fluid-button-y text-fluid-sm font-semibold text-white hover:bg-[#1E2A74] disabled:opacity-50"
          >
            {uploading
              ? "Uploading…"
              : `Upload${files.length > 1 ? ` (${files.length})` : ""}`}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
